import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, applyRoleLabels } from "../lib/api";
import { msalInstance } from "../lib/msal";

const Ctx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount: try to authenticate via httpOnly cookie. Endpoint returns null (not 401) when no session — keeps console clean.
  useEffect(() => {
    let mounted = true;
    const devWarn = (label, err) => {
      if (process.env.NODE_ENV !== "production") {
        console.warn(label, err?.message || err);
      }
    };
    // Always load role label overrides (public endpoint)
    api.get("/settings/role-labels")
      .then(r => { if (mounted) applyRoleLabels(r.data); })
      .catch(err => devWarn("Role labels fetch failed:", err));
    api.get("/auth/me")
      .then((r) => { if (mounted && r.data) setUser(r.data); })
      .catch((err) => devWarn("Auth probe failed:", err))
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const login = useCallback(async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    setUser(r.data.user);
    return r.data.user;
  }, []);

  const ssoLogin = useCallback(async (accessToken, email, name) => {
    const r = await api.post("/auth/msal-login", { access_token: accessToken, email, name: name || "" });
    setUser(r.data.user);
    return r.data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Cookie will still be cleared client-side; backend may be unreachable
      if (process.env.NODE_ENV !== "production") {
        console.warn("Logout API call failed; clearing local session anyway:", err?.message || err);
      }
    }
    // Iter75h: očisti MSAL cache i active account — inače Login.jsx useEffect
    // (koji prati `accounts` iz useMsal) odmah detektira cached SSO račun i re-loguira usera.
    try {
      const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
      if (account) {
        // logoutRedirect s onRedirectNavigate=false → očisti lokalni cache BEZ preusmjeravanja
        // na Microsoft logout stranicu. Korisnik ostaje u aplikaciji.
        await msalInstance.logoutRedirect({
          account,
          onRedirectNavigate: () => false,
        });
      } else if (typeof msalInstance.clearCache === "function") {
        await msalInstance.clearCache();
      }
      msalInstance.setActiveAccount(null);
    } catch (err) {
      console.debug("[Auth] MSAL cache clear during logout:", err?.message);
    }
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, ssoLogin, logout }),
    [user, loading, login, ssoLogin, logout]
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useAuth = () => useContext(Ctx);
